import React from 'react'
import { graphql } from 'gatsby'
import { Helmet } from 'react-helmet'
import moment from 'moment'
import md5 from 'md5'
import PropTypes from 'prop-types'
import MDXRenderer from 'gatsby-mdx/mdx-renderer'
import { BlogPageHeader, Footer } from '../components'
import * as styles from '../styles/pages/blogTemplate'
import * as base from '../styles/base'
import favicon from '../../assets/aito-favicon-128.png'

const propTypes = {
  data: PropTypes.shape({
    frontmatter: PropTypes.shape({
      path: PropTypes.string,
      title: PropTypes.string,
      introduction: PropTypes.string,
      tags: PropTypes.array,
      imgLink: PropTypes.string,
      date: PropTypes.string
    })
  }).isRequired
}

export default function BlogTemplate({ data }) {
  const { frontmatter, code, timeToRead } = data.mdx

  const entryDate = moment(frontmatter.date).format('MMMM D, YYYY')

  const authorEmailHash = md5(frontmatter.authorEmail)
  const defaultAvatar = encodeURI('https://s3.eu-central-1.amazonaws.com/aitoai-public/website-assets/avatar-default.png')
  const authorImgLink = `https://www.gravatar.com/avatar/${authorEmailHash}?s=500&d=${defaultAvatar}`

  return (
    <styles.PageWrapper>
      <Helmet>
        <meta charSet="utf-8" />
        <link rel="icon" href={favicon} type="image/png" />
        <title>{`Aito.ai - ${frontmatter.title}`}</title>
        {/* Twitter Card data */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Aito.ai" />
        <meta name="twitter:description" content={frontmatter.introduction} />
        <meta name="twitter:image" content="https://s3.eu-central-1.amazonaws.com/aitoai-public/website-assets/aito-social-image.png" />

        {/* Open Graph data */}
        <meta property="og:title" content="Aito.ai" />
        <meta property="og:url" content="http://aito.ai/" />
        <meta property="og:image" content="https://s3.eu-central-1.amazonaws.com/aitoai-public/website-assets/aito-social-image.png" />
        <meta property="og:description" content={frontmatter.introduction} />

        {/* GA verification */}
        <meta name="google-site-verification" content="3htXrCXwKfCC8VxBdDxetHss_cb5CzvA4annUIwGdEw" />
        <meta name="description" content={frontmatter.introduction} />
      </Helmet>
      <BlogPageHeader
        blogTitle={frontmatter.title}
        blogTags={frontmatter.tags}
        coverImgLink={frontmatter.coverImgLink}
      />
      <styles.MetadataContainer>
        <styles.AuthorImg src={authorImgLink} alt="author" />
        <styles.BlogMetadata>
          <styles.AuthorName>{frontmatter.authorName}</styles.AuthorName>
          <styles.MetadataText>{frontmatter.authorProfession}</styles.MetadataText>
          <styles.MetadataText>{`${entryDate} • ${timeToRead} min read`}</styles.MetadataText>
        </styles.BlogMetadata>
      </styles.MetadataContainer>
      <styles.Container>
        <base.MdContainer>
          <MDXRenderer>
            {code.body}
          </MDXRenderer>
          <styles.StyledLink to="/blog">
            <styles.BackArrow />
            Back to blog list
          </styles.StyledLink>
        </base.MdContainer>
      </styles.Container>
      <Footer />
    </styles.PageWrapper>
  )
}

BlogTemplate.propTypes = propTypes

export const pageQuery = graphql`
  query BlogPostByPath($path: String!) {
    mdx(frontmatter: { path: { eq: $path } }) {
      code {
        body
      }
      timeToRead
      frontmatter {
        path
        title
        introduction
        tags
        coverImgLink
        date
        authorName
        authorProfession
        authorEmail
      }
    }
  }
`
